import type { LinkItemDto } from '@/lib/sanity/dto';
import { isLocale, type Locale } from '@/lib/i18n';
import { stegaClean } from '@/lib/utils/stega';

export function hasLinkTarget(item?: LinkItemDto | null): boolean {
    if (!item) return false;

    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return !!href;
    }

    const slug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';
    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';

    return !!(slug || anchor);
}

/**
 * Locale prefix for an internal link. The referenced document's own language
 * wins over the current request, so a link that points across languages lands on
 * the right tree instead of rendering foreign content under the wrong prefix.
 * Anchor-only items have no target document and stay on the current locale.
 */
function resolveLinkLocale(item: LinkItemDto, locale: Locale): Locale {
    const targetLanguage = item.page?.language ? stegaClean(item.page.language).trim() : '';
    return isLocale(targetLanguage) ? targetLanguage : locale;
}

export function resolveHref(item: LinkItemDto, locale: Locale, queryParams?: Record<string, string>): string {
    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return href ?? '#';
    }

    const linkLocale = resolveLinkLocale(item, locale);
    const pageSlug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';

    // No slug means the start page of that language, which lives at the bare prefix.
    let base = pageSlug ? `/${linkLocale}/${pageSlug}` : `/${linkLocale}`;

    if (item.openInModal) {
        base = pageSlug ? `/${linkLocale}/m/${pageSlug}` : `/${linkLocale}/m`;
    }

    if (queryParams && Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value) searchParams.append(key, value);
        });
        const queryString = searchParams.toString();
        if (queryString) {
            const separator = base.includes('?') ? '&' : '?';
            base = `${base}${separator}${queryString}`;
        }
    }

    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';
    if (anchor) {
        return `${base}#${anchor}`;
    }

    return base;
}

export function getAnchorId(href: string): string | null {
    const i = href.indexOf('#');
    return i === -1 ? null : href.slice(i + 1) || null;
}

export function getAnchorIds(items: LinkItemDto[], locale: Locale): string[] {
    const ids = items.map((item) => getAnchorId(resolveHref(item, locale))).filter((id): id is string => Boolean(id));

    return Array.from(new Set(ids));
}

/** Whether a nav item is active for the current location. Anchor links only
 *  track scroll position on their own base page; on sub-pages they never match. */
export function isNavItemActive(item: LinkItemDto, locale: Locale, pathname: string, activeAnchorId: string): boolean {
    const basePath = resolveHref(item, locale).split('#')[0];

    if (item.anchor) {
        return pathname === basePath && item.anchor === activeAnchorId;
    }

    return pathname === basePath;
}
